import { Route } from "src/config/routes";

/**
 * Routes that stay reachable while the account has no portal role: only screens that need no
 * Hasura role. Logout is a public route outside Customer and does not depend on this list.
 */
export const routesWithoutPortalRole: readonly Route[] = [Route.profile, Route.passwordchange];

/**
 * What Hasura answers to every query of a user without a portal role. The portal does not send the
 * queries in this state (it skips them), so the text is shown as the known cause for support and
 * developers, who search for it, and is not a live response.
 */
export const HASURA_NO_ROLE_MESSAGE = "Your requested role is not in allowed roles";

/**
 * The outcomes of the access decision after login. Further cases (wrong customer, inactive customer,
 * not-activated user, system user) get added here once their behaviour is verified and the wording
 * is approved. Anything not recognised is UnknownError and keeps the existing error card.
 */
export enum AccessCase {
    NoPortalRole = "noPortalRole",
    UnknownError = "unknownError",
    Loading = "loading",
    Granted = "granted",
    NoCustomer = "noCustomer"
}

export interface AccessQueryState {
    failed: boolean;
    loading: boolean;
    customerFound: boolean;
}

/**
 * Decides which screen the account gets. Precedence: no portal role, failed query, loading,
 * customer found, no customer.
 *
 * A user whose token carries no role from RolesMap has no portal role: every Hasura query
 * is then denied with "Your requested role is not in allowed roles". Detected by user state,
 * not by the error text (#24872). An undefined or null user is not "no portal role".
 *
 * The query state is optional: before the queries run (skip) and in the profile no query state
 * exists, and only the NoPortalRole result is of interest there.
 */
export const classifyAccess = (
    user: { availableRoles: readonly unknown[] } | undefined | null,
    query: AccessQueryState = { failed: false, loading: false, customerFound: false }
): AccessCase => {
    if (!!user && user.availableRoles.length === 0) {
        return AccessCase.NoPortalRole;
    }
    if (query.failed) {
        return AccessCase.UnknownError;
    }
    if (query.loading) {
        return AccessCase.Loading;
    }
    return query.customerFound ? AccessCase.Granted : AccessCase.NoCustomer;
};
